// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for creating event groups that represent multi-step processes.
 * Provides functionality to create structured sequences of events, such as
 * shipping processes or maintenance routines, with origin and destination
 * tracking and configurable properties for each step.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { eventGroupSchema } from '@/asset/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';

/**
 * Schema for validating the API response when creating an event group.
 * Transforms the response to provide a more intuitive structure with
 * the event group data separated from metadata.
 */
const createEventGroupResponseSchema = z
  .object({
    data: eventGroupSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, eventGroup: data }));

type CreateEventGroupResponse = z.infer<typeof createEventGroupResponseSchema>;

/**
 * Property configuration for events within a group.
 * Supports both simple string values and structured tracking information
 * for shipping-related events.
 *
 * @typedef {Object} CreateEventGroupProperty
 * @property {string} propertyId - Unique identifier for the property type
 * @property {string | Object} value - Property value, either a string or tracking details
 * @property {string | null} value.trackingNumber - Optional tracking number for shipments
 * @property {string | null} value.carrier - Optional carrier identifier for shipments
 */
export interface CreateEventGroupProperty {
  propertyId: string;
  value: string | { trackingNumber: string | null; carrier: string | null };
}

/**
 * Parameters for creating a new event group.
 * Defines the complete structure of a multi-step process including
 * origin, destination, and the sequence of events with their properties.
 *
 * @typedef {Object} CreateEventGroupParameters
 * @property {string} originId - Identifier for the starting location
 * @property {string} destinationId - Identifier for the ending location
 * @property {Object[]} events - Array of events that make up the process
 * @property {string} events[].typeId - Type identifier for each event
 * @property {CreateEventGroupProperty[]} events[].properties - Properties for each event
 * @property {DateTime | null} [events[].occurredAt] - Optional timestamp when event occurred
 * @property {DateTime | null} [events[].expectedAt] - Optional timestamp when event is expected
 * @property {DateTime | null} [events[].promiseDate] - Optional date when event is promised
 */
export interface CreateEventGroupParameters {
  originId: string;
  destinationId: string;
  events: {
    typeId: string;
    properties: CreateEventGroupProperty[];
    occurredAt?: DateTime | null;
    expectedAt?: DateTime | null;
    promiseDate?: DateTime | null;
  }[];
}

/**
 * Creates a new event group for tracking multi-step processes.
 * This function handles the API call to create structured sequences of events
 * and validates the response data using Zod schemas. It's used for processes
 * like shipping or maintenance that involve multiple steps with specific
 * properties and timing requirements.
 *
 * @param assetId - Unique identifier of the asset to create the event group for
 * @param configurationId - Identifier of the event group configuration to use
 * @param parameters - Object containing the event group details
 * @returns Promise resolving to the created event group data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function createEventGroup(
  assetId: string,
  configurationId: string,
  parameters: CreateEventGroupParameters
): Promise<CreateEventGroupResponse> {
  const url = `/v1/asset/${assetId}/event-group`;
  const response = await api.post(url, {
    configurationId,
    events: parameters.events.map((event) => ({
      ...event,
      occurredAt:
        event.occurredAt === undefined || event.occurredAt === null
          ? event.occurredAt
          : event.occurredAt.toUTC().toISO(),
      expectedAt:
        event.expectedAt === undefined || event.expectedAt === null
          ? event.expectedAt
          : event.expectedAt.toUTC().toISO(),
      promiseDate: serializeDateForAPI(event.promiseDate),
    })),
    originId: parameters.originId,
    destinationId: parameters.destinationId,
  });

  return parseResponse(createEventGroupResponseSchema, response.data, {
    schemaName: 'CreateEventGroup',
    endpoint: url,
  });
}
